/**
 * Validation System v2.11.0
 * PSP/CRE Tool v3.0 - Calculation Engine
 * v2.11.0 (March 24, 2026) — BUG-194: Added BRIGADE echelon singular path to both
 *   brigade resolver sites. forceBuilderResult.brigade (singular) now wrapped in
 *   array as third fallback. Eliminates false "No brigades in force structure" error
 *   in Pre-Flight 4G validation when running at BRIGADE echelon.
 * 
 * REFACTORED VERSION - Imports from constants (no hardcoded values)
 * 
 * Purpose: Cross-tab validation and pre-flight checks before Tab 5
 * - Validates data integrity across Tabs 1-4
 * - Checks configuration completeness
 * - Generates traffic light status (Green/Yellow/Red)
 * - Provides actionable recommendations
 * 
 * Author: Dr. Tatar Radu MD
 * Date: November 14, 2024
 * Version: 2.13 - BUG-089: Gated 2 diagnostic console.log statements behind DEBUG constant
 *   (false by default). Fires 4× per tab switch on every render — now silent in production.
 *   Affected logs: "✅ Validation calculated MDOS from supply_chain" and
 *   "⚠️ Validation MDOS from SupplyIntelligence (Tab4 not saved)".
 *   console.error and console.warn entries remain ungated.
 *
 * Version: 2.12 - MDOS-VAL-001: Fix all three SupplyIntelligence MDOS fallback sites (March 7, 2026)
 *   ROOT CAUSE: SupplyIntelligence.calculateScenarioMDOS uses population-based consumption rates
 *   (~11% of correct rate) → produces 74-day MDOS fiction for a 10-day operation. This was used
 *   as fallback in three sites: validateTab4Logistics, validateCrossTabIntegration, getQuickValidationStatus.
 *   FIX: Insert new priority between mdos_analysis cache and SupplyIntelligence:
 *     1. mdos_analysis.overall_mdos — after Tab4 v8.5.3, this is calculator-derived (correct).
 *     2. casualty_projections.supply_chain.days_remaining — min across all blood products.
 *        Written by calculateCasualties (Tab3 Save) using actual T1/T2 rates. Correct even before Tab4 saved.
 *     3. SupplyIntelligence.calculateScenarioMDOS — last resort only, logged with ⚠️ warning.
 *   Console log at site 1 changed from ✅ to ⚠️ when SupplyIntelligence path fires (signals stale data).
 *
 * Version: 2.11 - C-009b: Exclude Role 1 from bed capacity total in validateCrossTabIntegration() (March 3, 2026)
 * Version: 2.10 - BUG FIX: brigade_configs deprecated path (February 25, 2026)
 *
 * v2.10 FIXES (February 25, 2026):
 * - BUG FIX (2 locations): brigade_configs was read from operation_parameters.brigade_configs
 *   (deprecated since Tab2 v7.0). Tab2 v7.0 removed that field entirely — brigades now live
 *   in force.brigades (canonical) or force.forceBuilderResult.brigades (fallback).
 *   LOCATION 1: calculateEstimatedBedNeed() — smart calc never found brigades → always used
 *     fallback ratio path. Fix: match casualtyCalculator.js v5.1.0+ multi-path lookup.
 *   LOCATION 2: validateTab2Operations() — always emitted ERROR "Brigade configurations not
 *     found" in Tab4 validation panel. Fix: same multi-path lookup. Error messages updated
 *     to point to Tab 1 (where brigades are built) instead of Tab 2.
 *
 * v2.9 FIXES (February 24, 2026 — Phase 6 second pass — all deferred items resolved):
 * - P1-07 NaN BUG FIX: BASE_CASUALTY_RATE = { daily: 0.015 } (object, not scalar).
 *   (1) `(BASE_CASUALTY_RATE / 1000) * ...` → NaN. Fixed to `BASE_CASUALTY_RATE.daily * ...`.
 *   (2) `baseCasualtyRate = BASE_CASUALTY_RATE` → entire smart calc returned NaN.
 *       Fixed to `BASE_CASUALTY_RATE.daily * 1000` (= 15 per 1000/day for formula).
 * - VS-01 FIX: Local CLS_PERCENTAGES { LOW:'10%', ... } removed (hardcoded duplicate).
 *   Now uses CLS_TRAINING_LEVELS[key].label from combatEffectivenessModifiers.js.
 * - VS-02 FIX: `evacRate = 0.5` → EVACUATION_RATE_DEFAULT from theatreHoldingPolicy.js.
 * - VS-03 FIX: Both postureModifier ternaries → OPERATIONAL_POSTURES[posture].casualty_multiplier.
 *   Old ternary string matching collapsed DELIBERATE_DEFENSE (0.6) to DEFENSE (0.7) — wrong.
 * - VS-06 FIX: `* 0.3` → BED_NEED_FACTORS.DNBI_BED_PERCENT; `= 0.6` → BATTLE_BED_PERCENT.
 * - VS-07 FIX: avgLOS ternary (broken keys) → AVG_LOS_BY_THP[thpPolicy] ?? AVG_LOS_DAYS_DEFAULT.
 * - VS-08 FIX: `beds_minimum_percent: 0.015` → BASE_CASUALTY_RATE.daily;
 *   `minimumBeds * 1.3` → minimumBeds * BED_NEED_FACTORS.SURGE_CAPACITY_MULTIPLIER.
 *
 * v2.8 FIXES (February 24, 2026 — Phase 6 first pass):
 * - VS-04 FIX (APP-RULES 6.1): Removed catch-block in calculateEstimatedBedNeed() that
 *   silently used hardcoded state modifiers (TRUE_RESERVE=0.05, SUPPORT=0.2, default=1.0)
 *   when getBattleCasualtyMultiplier() threw. Now re-throws with context so the root
 *   cause is visible instead of masked by wrong numbers.
 * - VS-05 FIX (APP-RULES 6.1): Same pattern for getDNBIRate() — removed catch-block
 *   fallback (RESERVE=0.5, SUPPORT=1.5, COMBAT=4.5). Now re-throws with context.
 * * v2.7 FIXES (February 22, 2026):
 * - P1-07 FIX: `baseCasualtyRate = 15` and `0.015 * 0.6 * 2.5` inline literals replaced
 *   with `BASE_CASUALTY_RATE` imported from casualtyCalculatorConstants.js.
 *   `BASE_CASUALTY_RATE` already existed in constants — APP_RULES 2.1 violation (hardcoded
 *   duplicate of a value that has a single authoritative source).
 *   Added import: BASE_CASUALTY_RATE from '../../constants/casualtyCalculatorConstants'.
 * - NOTED (Phase 6, VS-04/VS-05): try/catch blocks around getBattleCasualtyMultiplier() and
 *   getDNBIRate() retain hardcoded fallback values in catch clauses — APP_RULES 6.1 violation.
 *   Will be resolved in Phase 6 alongside postureModifier and avgLOS literals.
 * 
 * v2.6 FIXES (January 19, 2025):
 * - DNBI FIX: getDNBIRate was imported but never used - NOW USED in bed calculation
 *   - Bed estimation now includes DNBI patients (30% need beds)
 *   - More accurate bed requirement calculation
 * 
 * v2.5 FIXES (January 18, 2025):
 * - CRIT-002 FIX: Added brigade_configs validation in validateTab2Operations()
 *   - Checks if brigade_configs exists and is a non-empty array
 *   - Pre-flight catches missing/empty brigade configs BEFORE calculator runs
 *   - Better error messages guide user to fix the issue
 * 
 * - HIGH-001 FIX: Smart bed validation in validateCrossTabIntegration()
 *   - OLD: Simple ratio (1.5-3% of force = beds needed) - WRONG
 *   - NEW: calculateEstimatedBedNeed() considers:
 *     - Operational states (COMBAT=1.0, SUPPORT=0.2, RESERVE=0.05)
 *     - Posture (DEFENSE=0.7× casualties vs ATTACK)
 *     - Theatre Holding Policy (affects length of stay)
 *     - Evacuation to higher echelon (reduces bed occupancy)
 *   - Example: Brigade in defense with artillery in rear
 *     - Old calc: 5000 × 1.5% = 75 beds minimum
 *     - New calc: ~30-40 beds (based on actual ops context)
 *   - 36 beds now correctly validated as ADEQUATE for defensive scenario
 * 
 * v2.4 FIXES (January 12, 2025):
 * - FIX: National standard property name - now checks both camelCase (nationalStandard)
 *   and snake_case (national_standard) since Tab1 saves camelCase
 * - FIX: Multinational detection - now checks divisionStandards object properly
 *   instead of forceBuilderResult.corps.divisions
 * - RESTORED: CLS training warning
 * - RESTORED: Tourniquet/morphine stock warnings
 * 
 * v2.2 FIXES (January 12, 2025):
 * 
 * FIX 1: Multinational Corps National Standard
 * - Skip "national standard not set" warning for multinational corps
 * - Multinational forces have per-unit standards, not corps-level
 * 
 * FIX 2: Threat Level Property Location
 * - Now checks multiple locations: ops.threat_level, ops.environmental_factors.threat_level,
 *   and ops.threat_assessment.threat_type
 * - Ensures Tab2's saved data is properly validated
 * 
 * v2.1 FIXES (January 10, 2025):
 * 
 * FIX 1: Force Strength Property Name
 * - Tab1 saves force strength as 'totalStrength' (camelCase)
 * - This file previously only read 'total_strength' (snake_case)
 * - Added getForceStrength() helper to read both property names
 * - Fixed all validation checks to use helper function
 * 
 * FIX 2: MDOS (Medical Days of Supply) Calculation
 * - Tab4 calculates MDOS using SupplyIntelligence.calculateScenarioMDOS()
 * - Tab4's currentScenario doesn't include mdos_analysis (circular dependency)
 * - This file previously looked for 'initial_stock.days_of_supply' (doesn't exist)
 * - BUG: Validation showed "Supply stock critically low: 0 days" but Tab4 displayed "10 days"
 * - FIX: Import SupplyIntelligence and calculate MDOS the same way Tab4 does
 * - validateTab4Logistics() now calls SupplyIntelligence.calculateScenarioMDOS()
 * - Cross-tab validation now calculates MDOS
 * - quickValidationCheck() now calculates MDOS
 * 
 * ARCHITECTURE:
 * - MDOS standards from classVIII.js
 * - RTD standards from theatreHoldingPolicy.js
 * - Force echelon standards from forceEchelons.js
 * - No hardcoded values in this file
 */

// ============================================================================
// IMPORTS FROM CONSTANTS
// ============================================================================

// CALC-010: required-constant reader (throws with the path instead of a literal fallback)
// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { resolveInput } from '../assumptionLedger.js';

import { requireNumber } from '../requireValue.js';

import {
  MDOS_STANDARDS,
  validateStockLevels
} from '../../constants/classVIII';

import {
  THEATRE_HOLDING_POLICY
} from '../../constants/theatreHoldingPolicy';

import {
  FORCE_ECHELONS,
  getEchelonConfig
} from '../../constants/forceEchelons';

import {
  NATIONAL_STANDARDS,
  getStandardConfig
} from '../../constants/nationalStandards';

// v2.1 FIX: Import SupplyIntelligence to calculate MDOS the same way Tab4 does
// This ensures validation sees the same MDOS value that Tab4 displays
import * as SupplyIntelligence from './supplyIntelligence';

// v2.5 FIX (HIGH-001): Import operational state modifiers for smarter bed validation
import {
  getBattleCasualtyMultiplier,
  getDNBIRate
} from '../../constants/operationalStates';

// P1-07 FIX: Import BASE_CASUALTY_RATE — authoritative source for 15/1000/day base rate.
// Previously hardcoded as `baseCasualtyRate = 15` and `0.015` inline literals.
import {
  BASE_CASUALTY_RATE
} from '../../constants/casualtyCalculatorConstants';

// NOTE: MTF_CAPABILITIES import removed - was unused in this file
// If MTF data needed in future, import MTF_CAPABILITIES_BY_ECHELON from mtfCapabilities.js

// Phase 6 imports — VS-01/02/03/06/07/08 constants
import {
  BED_NEED_FACTORS
} from '../../constants/classVIII';

import {
  CLS_TRAINING_LEVELS
} from '../../constants/combatEffectivenessModifiers';

import {
  EVACUATION_RATE_DEFAULT,
  AVG_LOS_BY_THP
} from '../../constants/theatreHoldingPolicy';

import {
  OPERATIONAL_POSTURES
} from '../../constants/operationsConfig';

// ============================================================================
// DEBUG FLAG
// BUG-089: Gate diagnostic console.log statements — set to true only during
// active development. All console.error and console.warn remain ungated.
// ============================================================================
const DEBUG = false;

// ============================================================================
// VALIDATION THRESHOLDS (from constants or defaults)
// ============================================================================

const getValidationThresholds = (nationalStandard = 'NATO') => {
  const config = getStandardConfig ? getStandardConfig(nationalStandard) : null;
  
  return {
    // Medical capacity thresholds
    beds_per_personnel_percent: resolveInput(config?.medical?.beds_per_personnel, 'beds_per_personnel', { zeroIsMissing: true }), // 3%
    // VS-08 FIX: Replace hardcoded 0.015 with BASE_CASUALTY_RATE.daily.
    // 0.015 = 1.5% per day = 15/1000 — identical to BASE_CASUALTY_RATE.daily.
    // NOTE: BASE_CASUALTY_RATE is an object { daily: 0.015 }, use .daily property.
    beds_minimum_percent: BASE_CASUALTY_RATE.daily,
    
    // Supply thresholds (from MDOS_STANDARDS)
    supply_minimum_days: requireNumber(MDOS_STANDARDS?.STATUS?.CRITICAL?.days, 'validationSystem', 'MDOS_STANDARDS.STATUS.CRITICAL.days'),
    supply_adequate_days: requireNumber(MDOS_STANDARDS?.STATUS?.ADEQUATE?.days, 'validationSystem', 'MDOS_STANDARDS.STATUS.ADEQUATE.days'),
    
    // Evacuation thresholds
    ambulances_per_personnel: 500, // 1 per 500
    helicopters_per_brigade: 2,    // Minimum air assets
    
    // Personnel thresholds
    medics_per_battalion: 10,
    surgeons_per_role2: 2
  };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * v2.1 FIX: Get force strength from force object
 * Handles both 'totalStrength' (camelCase from Tab1) and 'total_strength' (snake_case)
 * 
 * @param {object} force - Force configuration object
 * @returns {number} Force strength or 0 if not set
 */
const getForceStrength = (force) => {
  if (!force) return 0;
  // Check camelCase first (Tab1 saves as totalStrength)
  if (force.totalStrength && force.totalStrength > 0) {
    return force.totalStrength;
  }
  // Fallback to snake_case
  if (force.total_strength && force.total_strength > 0) {
    return force.total_strength;
  }
  // Try forceBuilderResult
  if (force.forceBuilderResult?.corps?.strength) {
    return force.forceBuilderResult.corps.strength;
  }
  return 0;
};

/**
 * v2.5 FIX (HIGH-001): Calculate estimated bed need using actual operational context
 * 
 * The old method used a flat ratio (1.5-3% of force = beds needed).
 * This was WRONG because it ignored:
 * - Unit types (HQ/Logistics/Artillery have much lower casualty rates)
 * - Operational states (RESERVE = 5% of combat casualty rate)
 * - Posture (DEFENSE = ~70% of attack casualty rate)
 * - THP (affects bed turnover)
 * - Evacuation to higher echelon (reduces bed occupancy)
 * 
 * @param {object} scenario - Complete scenario object
 * @returns {object} { estimated_beds_needed, calculation_details, using_smart_calc }
 */
const calculateEstimatedBedNeed = (scenario) => {
  const result = {
    estimated_beds_needed: 0,
    calculation_details: [],
    using_smart_calc: false,
    fallback_reason: null
  };

  const forceSize = getForceStrength(scenario.force);
  if (forceSize <= 0) {
    result.fallback_reason = 'No force strength';
    return result;
  }

  // v2.10 FIX: Use canonical brigade path (Tab2 v7.0 removed operation_parameters.brigade_configs).
  // Match the same multi-location defensive lookup used by casualtyCalculator.js v5.1.0+:
  //   PRIMARY:  force.brigades           (flattened — Tab1/Tab2 v3.0, current canonical path)
  //   FALLBACK: force.forceBuilderResult.brigades (nested — backward compatibility)
  //   F2-ARCH-010: ForceService now populates brigades[] for all echelons.
  //   Singular forceBuilderResult.brigade fallback kept for pre-F2 persisted data only.
  const _fbrVs1 = scenario.force?.forceBuilderResult;
  const brigadeConfigs =
    Array.isArray(scenario.force?.brigades) && scenario.force.brigades.length > 0
      ? scenario.force.brigades
      : Array.isArray(_fbrVs1?.brigades) && _fbrVs1.brigades.length > 0
        ? _fbrVs1.brigades
        : _fbrVs1?.brigade
          ? [_fbrVs1.brigade]   // legacy: pre-F2 persisted BRIGADE echelon data
          : null;
  
  if (!Array.isArray(brigadeConfigs) || brigadeConfigs.length === 0) {
    // Fallback: Use simple ratio but with posture adjustment
    result.fallback_reason = 'No brigade configs available';
    const posture = scenario.operation_parameters?.operational_posture || 'DELIBERATE_DEFENSE';
    // VS-03 FIX: Replace ternary with OPERATIONAL_POSTURES[posture].casualty_multiplier.
    // Old ternary: includes('DEFENSE')→0.7, includes('ATTACK')→1.0, else→0.85.
    // Uses string matching which breaks for multi-word postures (DELIBERATE_DEFENSE = 0.6,
    // not 0.7). OPERATIONAL_POSTURES has exact multiplier per posture key.
    // Fallback 0.85 retained as default for unknown postures (between ATTACK=1.0 and DEFENSE=0.7).
    const postureModifier = OPERATIONAL_POSTURES[posture]?.casualty_multiplier ?? 0.85;
    
    // P1-07 FIX: Use BASE_CASUALTY_RATE.daily (object property, not division).
    // BASE_CASUALTY_RATE = { daily: 0.015 } — using / 1000 would produce NaN.
    // Old code: `const baseRate = 0.015 * 0.6 * 2.5 * postureModifier`
    const baseRate = BASE_CASUALTY_RATE.daily * 0.6 * 2.5 * postureModifier;
    result.estimated_beds_needed = Math.ceil(forceSize * baseRate);
    result.calculation_details.push({
      method: 'FALLBACK_RATIO',
      formula: `${forceSize} × ${(baseRate * 100).toFixed(2)}% = ${result.estimated_beds_needed} beds`,
      posture_modifier: postureModifier
    });
    return result;
  }

  // Smart calculation using actual brigade configs
  result.using_smart_calc = true;
  
  // Get THP for average length of stay
  const thpPolicy = scenario.logistics?.theatre_holding_policy || 'STANDARD';
  // VS-07 FIX: Replace inline ternary with AVG_LOS_BY_THP lookup from theatreHoldingPolicy.js.
  // Old ternary used wrong keys ('EXTENDED', 'MINIMUM') that don't match actual THP policy
  // values ('THP_7_DAY', 'THP_3_DAY', etc.) — always fell through to default 2.5.
  // AVG_LOS_BY_THP is keyed by actual thpPolicy values; BED_NEED_FACTORS.AVG_LOS_DAYS_DEFAULT
  // provides the fallback (2.5 days) when policy key is not found.
  const avgLOS = AVG_LOS_BY_THP[thpPolicy] ?? BED_NEED_FACTORS.AVG_LOS_DAYS_DEFAULT;
  
  // VS-02 FIX: Replace hardcoded `evacRate = 0.5` with EVACUATION_RATE_DEFAULT from
  // theatreHoldingPolicy.js. EVACUATION_RATE_DEFAULT = 0.50 (NATO planning standard
  // for % of T1/T2 evacuated to higher echelon within 24h — reduces bed occupancy).
  const evacRate = EVACUATION_RATE_DEFAULT;

  // P1-07 + NaN FIX: BASE_CASUALTY_RATE is an object { daily: 0.015 }.
  // Formula uses (brigadeStrength / 1000) * baseCasualtyRate, so we need per-1000 value.
  // BASE_CASUALTY_RATE.daily * 1000 = 15 per 1000 per day (= 1.5% baseline rate).
  // Old code: `const baseCasualtyRate = BASE_CASUALTY_RATE` → object → NaN.
  const baseCasualtyRate = BASE_CASUALTY_RATE.daily * 1000;
  
  let totalEstimatedDailyCasualties = 0;
  
  for (const brigadeConfig of brigadeConfigs) {
    const brigadeStrength = resolveInput(brigadeConfig.strength, 'brigade_strength', { zeroIsMissing: true });
    const operationalState = brigadeConfig.operational_state || 'COMBAT_ENGAGED';
    
    // Get state modifier for BATTLE casualties (COMBAT=1.0, SUPPORT=0.2, RESERVE=0.05)
    // VS-04 FIX (APP-RULES 6.1): Removed catch-block fallback that silently used
    // hardcoded TRUE_RESERVE=0.05, SUPPORT_ACTIVE=0.2, default=1.0.
    // Fallback mechanisms mask bugs — throw with context so the root cause is visible.
    let stateModifier;
    try {
      stateModifier = getBattleCasualtyMultiplier(operationalState);
    } catch (e) {
      throw new Error(
        `calculateEstimatedBedNeed: getBattleCasualtyMultiplier('${operationalState}') ` +
        `failed — ${e.message}. Verify operationalStates.js exports this function ` +
        `and that '${operationalState}' is a valid operational state key.`
      );
    }

    // v2.5 FIX: Get DNBI rate (per 1000 per day)
    // VS-05 FIX (APP-RULES 6.1): Removed catch-block fallback that silently used
    // hardcoded TRUE_RESERVE=0.5, SUPPORT_ACTIVE=1.5, COMBAT=4.5.
    // Throw with context — the caller must fix the data, not silently receive wrong rates.
    let dnbiRate;
    try {
      dnbiRate = getDNBIRate(operationalState);
    } catch (e) {
      throw new Error(
        `calculateEstimatedBedNeed: getDNBIRate('${operationalState}') ` +
        `failed — ${e.message}. Verify operationalStates.js exports getDNBIRate ` +
        `and that '${operationalState}' is a valid operational state key.`
      );
    }
    
    // Get posture modifier from brigade config
    const posture = brigadeConfig.operation?.operational_posture || 'DELIBERATE_DEFENSE';
    // VS-03 FIX: Same as fallback block — use OPERATIONAL_POSTURES[posture].casualty_multiplier.
    // Old ternary added RESERVE check (0.3) which maps to CONSOLIDATED_DEFENSE (0.3) or
    // STATIC_DEFENSE in OPERATIONAL_POSTURES — now resolved precisely from constants.
    const postureModifier = OPERATIONAL_POSTURES[posture]?.casualty_multiplier ?? 0.85;
    
    // Calculate this brigade's BATTLE casualties
    const brigadeBattleCasualties = (brigadeStrength / 1000) * baseCasualtyRate * stateModifier * postureModifier;
    
    // VS-06 FIX: Replace hardcoded 0.3 with BED_NEED_FACTORS.DNBI_BED_PERCENT (= 0.30).
    // 30% of DNBI casualties require a bed; the other 70% are ambulatory treatment only.
    const brigadeDNBI = (brigadeStrength / 1000) * dnbiRate * BED_NEED_FACTORS.DNBI_BED_PERCENT;
    
    // Total casualties needing beds
    const brigadeDailyCasualties = brigadeBattleCasualties + brigadeDNBI;
    totalEstimatedDailyCasualties += brigadeDailyCasualties;
    
    result.calculation_details.push({
      brigade: brigadeConfig.brigade_name || 'Unknown',
      strength: brigadeStrength,
      operational_state: operationalState,
      state_modifier: stateModifier,
      dnbi_rate: dnbiRate,
      posture: posture,
      posture_modifier: postureModifier,
      daily_battle_casualties: Math.round(brigadeBattleCasualties * 10) / 10,
      daily_dnbi_needing_beds: Math.round(brigadeDNBI * 10) / 10,
      daily_total: Math.round(brigadeDailyCasualties * 10) / 10
    });
  }
  
  // VS-06 FIX: Replace hardcoded needsBedsFactor = 0.6 with BED_NEED_FACTORS.BATTLE_BED_PERCENT.
  // 60% of battle casualties require a bed (T1 evacuated or OR, T2/T3 held).
  const needsBedsFactor = BED_NEED_FACTORS.BATTLE_BED_PERCENT;
  
  result.estimated_beds_needed = Math.ceil(
    totalEstimatedDailyCasualties * needsBedsFactor * avgLOS * (1 - evacRate * 0.5)
  );
  
  result.calculation_details.push({
    method: 'SMART_CALCULATION',
    total_daily_casualties: Math.round(totalEstimatedDailyCasualties * 10) / 10,
    needs_beds_factor: needsBedsFactor,
    avg_length_of_stay: avgLOS,
    evacuation_factor: evacRate,
    thp_policy: thpPolicy
  });
  
  return result;
};

// ============================================================================
// MAIN VALIDATION ORCHESTRATOR
// ============================================================================

/**
 * Run complete validation across all tabs
 * @param {object} scenario - Complete scenario object
 * @returns {object} Comprehensive validation results
 */
export function runCompleteValidation(scenario) {
  const validation = {
    timestamp: new Date().toISOString(),
    overall_status: 'green',
    can_proceed_to_tab5: true,
    checks: {
      tab1_force: null,
      tab2_operations: null,
      tab3_medical: null,
      tab4_logistics: null,
      cross_tab_integration: null
    },
    summary: {
      total_checks: 0,
      passed: 0,
      warnings: 0,
      errors: 0
    },
    recommendations: []
  };

  if (!scenario) {
    validation.overall_status = 'red';
    validation.can_proceed_to_tab5 = false;
    validation.summary.errors = 1;
    validation.recommendations.push({
      priority: 'CRITICAL',
      message: 'No scenario data found',
      action: 'Start from Tab 1 to create a scenario'
    });
    return validation;
  }

  try {
    const nationalStandard = scenario.force?.national_standard || 'NATO';
    const thresholds = getValidationThresholds(nationalStandard);

    // Run individual tab validations
    validation.checks.tab1_force = validateTab1Force(scenario, thresholds);
    validation.checks.tab2_operations = validateTab2Operations(scenario, thresholds);
    validation.checks.tab3_medical = validateTab3Medical(scenario, thresholds);
    validation.checks.tab4_logistics = validateTab4Logistics(scenario, thresholds);
    validation.checks.cross_tab_integration = validateCrossTabIntegration(scenario, thresholds);

    // Aggregate results
    const allChecks = Object.values(validation.checks);
    allChecks.forEach(check => {
      if (check) {
        validation.summary.total_checks++;
        if (check.status === 'passed') {
          validation.summary.passed++;
        } else if (check.status === 'warning') {
          validation.summary.warnings++;
        } else if (check.status === 'error') {
          validation.summary.errors++;
        }
      }
    });

    // Determine overall status
    if (validation.summary.errors > 0) {
      validation.overall_status = 'red';
      validation.can_proceed_to_tab5 = false;
    } else if (validation.summary.warnings > 0) {
      validation.overall_status = 'yellow';
      validation.can_proceed_to_tab5 = true;
    } else {
      validation.overall_status = 'green';
      validation.can_proceed_to_tab5 = true;
    }

    // Generate recommendations
    validation.recommendations = generateValidationRecommendations(validation);

    return validation;
  } catch (error) {
    console.error('Error running complete validation:', error);
    validation.overall_status = 'red';
    validation.can_proceed_to_tab5 = false;
    validation.summary.errors++;
    validation.recommendations.push({
      priority: 'CRITICAL',
      message: 'Validation system error',
      action: 'Check browser console for details'
    });
    return validation;
  }
}

// ============================================================================
// TAB 1 VALIDATION: FORCE COMPOSITION
// ============================================================================

function validateTab1Force(scenario, thresholds) {
  const result = {
    tab: 'Tab 1: Force Composition',
    status: 'passed',
    issues: [],
    details: {}
  };

  if (!scenario.force) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'force',
      message: 'Force composition not configured',
      fix: 'Complete Tab 1: Force Composition'
    });
    return result;
  }

  const force = scenario.force;

  // Validate echelon (using FORCE_ECHELONS if available)
  if (!force.echelon) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'echelon',
      message: 'Force echelon not specified',
      fix: 'Select force echelon (Battalion/Brigade/Division/Corps)'
    });
  } else if (FORCE_ECHELONS && !FORCE_ECHELONS[force.echelon]) {
    // Validate echelon value against FORCE_ECHELONS
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'echelon',
      message: `Invalid force echelon: ${force.echelon}`,
      fix: `Select valid echelon: ${Object.keys(FORCE_ECHELONS).join(', ')}`
    });
  } else {
    const echelonConfig = getEchelonConfig ? getEchelonConfig(force.echelon) : null;
    if (echelonConfig) {
      result.details.echelon_info = {
        label: echelonConfig.label,
        typical_strength: echelonConfig.typical_strength
      };
    }
  }

  // Validate total strength - v2.1 FIX: Use helper for both property name support
  const forceStrength = getForceStrength(force);
  if (!forceStrength || forceStrength === 0) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'total_strength',
      message: 'Total force strength is zero or not set',
      fix: 'Enter total personnel strength'
    });
  } else {
    result.details.force_strength = forceStrength;
    result.details.echelon = force.echelon;
    
    // Check if strength matches echelon (warning only)
    const echelonConfig = getEchelonConfig ? getEchelonConfig(force.echelon) : null;
    if (echelonConfig && echelonConfig.typical_strength) {
      const ratio = forceStrength / echelonConfig.typical_strength;
      if (ratio < 0.5 || ratio > 2.0) {
        result.issues.push({
          severity: 'WARNING',
          field: 'strength_echelon_mismatch',
          message: `Force strength ${forceStrength} unusual for ${force.echelon} (typical: ${echelonConfig.typical_strength})`,
          fix: 'Verify echelon selection matches force size'
        });
        if (result.status === 'passed') result.status = 'warning';
      }
    }
  }

  // Validate national standard
  // v2.3 FIX: Check both property name formats (camelCase from Tab1, snake_case legacy)
  const corpNationalStandard = force.nationalStandard || force.national_standard;
  
  // v2.3 FIX: Detect multinational corps by checking divisionStandards or forceBuilderResult
  const divisionStandards = force.divisionStandards || {};
  const divisionStandardValues = Object.values(divisionStandards);
  const hasMultipleDivisionStandards = divisionStandardValues.length > 1 && 
    new Set(divisionStandardValues).size > 1;
  
  const isMultinational = force.echelon === 'CORPS' && 
    (corpNationalStandard === 'MULTINATIONAL' ||
     hasMultipleDivisionStandards ||
     force.multinational === true ||
     force.forceBuilderResult?.corps?.divisions?.some(d => 
       d.nationalStandard && d.nationalStandard !== corpNationalStandard));
  
  if (!corpNationalStandard && !isMultinational) {
    result.issues.push({
      severity: 'WARNING',
      field: 'national_standard',
      message: 'National standard not set (defaulting to NATO)',
      fix: 'Select national standard in Tab 1'
    });
    if (result.status === 'passed') result.status = 'warning';
  } else if (isMultinational) {
    // Multinational - this is fine, just note it
    result.details.multinational = true;
    result.details.division_standards = divisionStandards;
  } else if (NATIONAL_STANDARDS && !NATIONAL_STANDARDS[corpNationalStandard]) {
    // Validate national_standard value against NATIONAL_STANDARDS
    result.issues.push({
      severity: 'ERROR',
      field: 'national_standard',
      message: `Invalid national standard: ${corpNationalStandard}`,
      fix: `Select valid standard: ${Object.keys(NATIONAL_STANDARDS).join(', ')}`
    });
    result.status = 'error';
  }

  // Validate CLS training percentage
  // v2.2: Check forceQuality.cls_training_percentage (where Tab1 saves it)
  const clsValue = force.forceQuality?.cls_training_percentage ||
                   force.cls_training_percentage || 
                   force.force_quality?.cls_training_percentage ||
                   force.forceBuilderResult?.corps?.force_quality?.cls_training_percentage;
  
  if (clsValue) {
    // VS-01 FIX: Replace local CLS_PERCENTAGES object with CLS_TRAINING_LEVELS from
    // combatEffectivenessModifiers.js. CLS_TRAINING_LEVELS[key].label = 'Low (10%)' etc.
    // Old local CLS_PERCENTAGES was a duplicate of the authoritative constant.
    if (CLS_TRAINING_LEVELS[clsValue]) {
      result.details.cls_training = CLS_TRAINING_LEVELS[clsValue].label;
    } else {
      // Try legacy CLS_XX format
      const clsMatch = clsValue.match && clsValue.match(/CLS_(\d+)/);
      result.details.cls_training = clsMatch ? `${clsMatch[1]}%` : clsValue;
    }
  } else {
    // v2.2 RESTORED: Warning when CLS not set - guides user to Tab1
    result.issues.push({
      severity: 'WARNING',
      field: 'cls_training_percentage',
      message: 'CLS training percentage not set (using default)',
      fix: 'Set the CLS training in Tab 3, card Care at the point of injury (moved from Tab 1 on 2026-10-25)'
    });
    if (result.status === 'passed') result.status = 'warning';
    result.details.cls_training = 'Not set (default 25%)';
  }

  return result;
}

// ============================================================================
// TAB 2 VALIDATION: OPERATIONS PARAMETERS
// ============================================================================

function validateTab2Operations(scenario, thresholds) {
  const result = {
    tab: 'Tab 2: Operations Configuration',
    status: 'passed',
    issues: [],
    details: {}
  };

  if (!scenario.operation_parameters) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'operation_parameters',
      message: 'Operation parameters not configured',
      fix: 'Complete Tab 2: Operations Configuration'
    });
    return result;
  }

  const ops = scenario.operation_parameters;

  // Check configured flag
  if (!ops.configured) {
    result.issues.push({
      severity: 'WARNING',
      field: 'configured',
      message: 'Operation parameters marked as incomplete',
      fix: 'Review and complete Tab 2'
    });
    if (result.status === 'passed') result.status = 'warning';
  }

  // Validate operation type
  if (!ops.operation_type) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'operation_type',
      message: 'Operation type not specified',
      fix: 'Select operation type (Ground Combat, Urban, etc.)'
    });
  }

  // Validate duration
  if (!ops.duration_days || ops.duration_days === 0) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'duration_days',
      message: 'Operation duration not set',
      fix: 'Enter operation duration in days'
    });
  } else if (ops.duration_days > 90) {
    result.issues.push({
      severity: 'WARNING',
      field: 'duration_days',
      message: 'Operation duration exceeds 90 days',
      fix: 'Long operations may require additional logistics planning'
    });
    if (result.status === 'passed') result.status = 'warning';
  } else {
    result.details.duration = ops.duration_days;
    result.details.operation_type = ops.operation_type;
  }

  // Validate operational posture
  if (!ops.operational_posture) {
    result.issues.push({
      severity: 'WARNING',
      field: 'operational_posture',
      message: 'Operational posture not specified',
      fix: 'Select operational posture (Attack/Defense/Movement)'
    });
    if (result.status === 'passed') result.status = 'warning';
  }

  // Validate threat level
  // v2.2 FIX: Check multiple locations where threat_level might be stored
  const threatLevel = ops.threat_level || 
                      ops.environmental_factors?.threat_level ||
                      ops.threat_assessment?.threat_type;
  
  if (!threatLevel) {
    result.issues.push({
      severity: 'WARNING',
      field: 'threat_level',
      message: 'Threat level not specified',
      fix: 'Select threat level for accurate casualty estimation'
    });
    if (result.status === 'passed') result.status = 'warning';
  } else {
    result.details.threat_level = threatLevel;
  }

  // v2.10 FIX: Use canonical brigade path. Tab2 v7.0 removed operation_parameters.brigade_configs.
  // CRIT-002: Calculator requires force.brigades (or forceBuilderResult.brigades) to be non-empty.
  // F2-ARCH-010: ForceService now populates brigades[] for all echelons.
  //   PRIMARY:  scenario.force.brigades (flattened — Tab1/Tab2 v3.0, current canonical path)
  //   FALLBACK: scenario.force.forceBuilderResult.brigades (array — all echelons post-F2)
  //   LEGACY:   scenario.force.forceBuilderResult.brigade (singular — pre-F2 persisted data)
  const _fbrVs2 = scenario.force?.forceBuilderResult;
  const brigadeConfigsFromForce =
    Array.isArray(scenario.force?.brigades) && scenario.force.brigades.length > 0
      ? scenario.force.brigades
      : Array.isArray(_fbrVs2?.brigades) && _fbrVs2.brigades.length > 0
        ? _fbrVs2.brigades
        : _fbrVs2?.brigade
          ? [_fbrVs2.brigade]   // legacy: pre-F2 persisted BRIGADE echelon data
          : null;
  if (!Array.isArray(brigadeConfigsFromForce)) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'brigade_configs',
      message: 'Brigade configurations not found',
      fix: 'Complete Tab 1: Build your force structure, then complete Tab 2 to configure operations'
    });
  } else if (brigadeConfigsFromForce.length === 0) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'brigade_configs',
      message: 'No brigades in force structure',
      fix: 'Return to Tab 1 and add at least one brigade to the force'
    });
  } else {
    result.details.brigade_count = brigadeConfigsFromForce.length;
    result.details.total_strength = brigadeConfigsFromForce.reduce(
      (sum, b) => sum + (b.strength || 4500), 0
    );
  }

  return result;
}

// ============================================================================
// TAB 3 VALIDATION: MEDICAL CONFIGURATION
// ============================================================================

function validateTab3Medical(scenario, thresholds) {
  const result = {
    tab: 'Tab 3: Medical Configuration',
    status: 'passed',
    issues: [],
    details: {}
  };

  if (!scenario.medical_configuration) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'medical_configuration',
      message: 'Medical configuration not configured',
      fix: 'Complete Tab 3: Medical Configuration'
    });
    return result;
  }

  const med = scenario.medical_configuration;
  const echelon = scenario.force?.echelon || 'BRIGADE';

  // BUG-T22 fix: bed count from per_facility_evacuation_assets (ForceBuilder path)
  // mtf_structure only has role1/role2/role3; R2E lives in per_facility_evacuation_assets.
  const perFacilities = med.per_facility_evacuation_assets || [];

  // Validate MTF structure
  if (!med.mtf_structure && perFacilities.length === 0) {
    result.status = 'error';
    result.issues.push({
      severity: 'ERROR',
      field: 'mtf_structure',
      message: 'MTF structure not configured',
      fix: 'Configure Medical Treatment Facilities in Tab 3'
    });
  } else {
    const mtf = med.mtf_structure || {};

    // Calculate total beds — primary: mtf_structure; fallback: per_facility_evacuation_assets
    let totalBeds = 0;
    let totalSurgicalTeams = 0;

    if (mtf.role2?.count > 0) {
      totalBeds += (mtf.role2.beds || 20) * mtf.role2.count;
      totalSurgicalTeams += (mtf.role2.surgical_teams || 2) * mtf.role2.count;
    }
    if (mtf.role2e?.count > 0) {
      totalBeds += (mtf.role2e.beds || 40) * mtf.role2e.count;
      totalSurgicalTeams += (mtf.role2e.surgical_teams || 4) * mtf.role2e.count;
    }
    if (mtf.role3?.count > 0) {
      totalBeds += (mtf.role3.beds || 100) * mtf.role3.count;
      totalSurgicalTeams += (mtf.role3.surgical_teams || 8) * mtf.role3.count;
    }

    // BUG-T22: fallback to per_facility_evacuation_assets when mtf_structure has no beds
    if (totalBeds === 0 && perFacilities.length > 0) {
      perFacilities.forEach(f => {
        const beds = f.organic_assets?.beds?.total
                  || f.organic_assets?.total_beds
                  || f.beds
                  || 0;
        totalBeds += beds;
        const teams = f.organic_assets?.surgical_teams?.total
                   || f.organic_assets?.surgical_teams
                   || 0;
        totalSurgicalTeams += teams;
      });
    }

    result.details.total_beds = totalBeds;
    result.details.surgical_teams = totalSurgicalTeams;

    if (totalBeds === 0) {
      result.status = 'error';
      result.issues.push({
        severity: 'ERROR',
        field: 'beds',
        message: 'No medical beds configured',
        fix: 'Add Role 2+ facilities with bed capacity in Tab 3'
      });
    }

    if (totalSurgicalTeams === 0) {
      result.issues.push({
        severity: 'WARNING',
        field: 'surgical_teams',
        message: 'No surgical teams configured',
        fix: 'Add surgical teams to Role 2+ facilities'
      });
      if (result.status === 'passed') result.status = 'warning';
    }

    // BUG-T22: R2E check — required at DIVISION and CORPS echelon
    if (['DIVISION', 'CORPS'].includes(echelon)) {
      // BUG-S81-003 FIX: Tab3 saves R2E facilities with type='ROLE2_ENHANCED'.
      // Previous patterns ('ROLE2E', '2E', 'ROLE_2E') all fail to match 'ROLE2_ENHANCED'
      // because '2_ENHANCED' does not contain '2E' or 'ROLE2E' as a substring.
      // Added 'ROLE2_ENHANCED' and '2_ENHANCED' patterns to catch the ForceBuilder path.
      const hasR2E = perFacilities.some(f => {
        const ft = (f.facility_type || f.type || '').toUpperCase();
        return ft.includes('ROLE2E') || ft.includes('ROLE2_ENHANCED') || ft.includes('2_ENHANCED') || ft.includes('ROLE_2E');
      }) || (mtf.role2e?.count > 0);
      if (!hasR2E) {
        result.issues.push({
          severity: 'WARNING',
          field: 'r2e',
          message: 'No Role 2 Enhanced (R2E) facility configured — required at division/corps echelon (AJP-4.10C §3.2)',
          fix: 'Configure at least one R2E facility in Tab 3'
        });
        if (result.status === 'passed') result.status = 'warning';
      }
    }
  }

  // Validate evacuation assets
  if (!med.evacuation_assets) {
    result.issues.push({
      severity: 'WARNING',
      field: 'evacuation_assets',
      message: 'Evacuation assets not configured',
      fix: 'Configure ground and air evacuation assets in Tab 3'
    });
    if (result.status === 'passed') result.status = 'warning';
  } else {
    const assets = med.evacuation_assets;
    const groundCount =
      (assets.ground?.light_ambulances?.count || 0) +
      (assets.ground?.armored_ambulances?.count || 0);
    const airCount =
      (assets.air?.iar330_puma?.count || 0) +
      (assets.air?.uh60_blackhawk?.count || 0) +
      (assets.air?.ch47_chinook?.count || 0);

    // BUG-T22: also count per-facility organic helicopter assets
    const perFacilityAirCount = perFacilities.reduce((sum, f) => {
      return sum + (f.organic_assets?.helicopters?.count || f.organic_assets?.helicopters?.allocated || 0);
    }, 0);

    result.details.ground_ambulances = groundCount;
    result.details.air_assets = airCount + perFacilityAirCount;

    if (groundCount === 0) {
      result.issues.push({
        severity: 'WARNING',
        field: 'ground_ambulances',
        message: 'No ground ambulances configured',
        fix: 'Add ground evacuation assets in Tab 3'
      });
      if (result.status === 'passed') result.status = 'warning';
    }

    // BUG-T22: air MEDEVAC check was missing entirely
    if (airCount === 0 && perFacilityAirCount === 0) {
      result.issues.push({
        severity: 'WARNING',
        field: 'air_medevac',
        message: 'No air MEDEVAC assets configured — MASCAL evacuation will be ground-only',
        fix: 'Add helicopter MEDEVAC assets (IAR-330, UH-60) in Tab 3'
      });
      if (result.status === 'passed') result.status = 'warning';
    }
  }

  // BUG-T22: Blood DOS check
  // Sentinel: blood is "calculable" if ANY of these are present/non-zero.
  // IMPORTANT: final_dos can legitimately be 0 on a stockout scenario (Day N stock = 0),
  // so we cannot use it as a boolean sentinel. Instead check:
  //   (a) projection array has entries  → calculator ran and produced blood data
  //   (b) prbc_units > 0               → Tab 4 saved with blood stock
  //   (c) legacy fields                → backward compat
  const proj = scenario.casualty_projections?.medical_requirements?.resupply_summary?.dos_projection;
  const bloodDos = med.blood_stock_dos
                || scenario.logistics?.medical_supplies?.mdos_analysis?.blood_dos
                || scenario.logistics?.medical_supplies?.initial_stock?.blood_units_dos
                || (proj?.projection?.length > 0 ? 1 : 0)   // calculator ran → data exists
                || scenario.logistics?.medical_supplies?.initial_stock?.prbc_units
                || 0;
  if (bloodDos === 0) {
    result.issues.push({
      severity: 'WARNING',
      field: 'blood_dos',
      message: 'Blood stock not configured — run Tab 3 Save & Continue first, then set blood quantities in Tab 4',
      fix: 'Complete Tab 3 Save & Continue (runs casualty calculator), then verify blood stock in Tab 4'
    });
    if (result.status === 'passed') result.status = 'warning';
  } else {
    result.details.blood_dos = bloodDos;
  }

  return result;
}

// ============================================================================
// TAB 4 VALIDATION: LOGISTICS
// Uses MDOS_STANDARDS from constants
// ============================================================================

function validateTab4Logistics(scenario, thresholds) {
  const result = {
    tab: 'Tab 4: Logistics Configuration',
    status: 'passed',
    issues: [],
    details: {}
  };

  if (!scenario.logistics) {
    result.issues.push({
      severity: 'WARNING',
      field: 'logistics',
      message: 'Logistics configuration not found (optional but recommended)',
      fix: 'Complete Tab 4: Logistics Configuration'
    });
    if (result.status === 'passed') result.status = 'warning';
    return result;
  }

  const log = scenario.logistics;

  // Validate medical supplies using MDOS_STANDARDS from constants
  if (!log.medical_supplies?.initial_stock) {
    result.issues.push({
      severity: 'WARNING',
      field: 'medical_supplies',
      message: 'Initial medical supply stock not configured',
      fix: 'Configure initial supply levels in Tab 4'
    });
    if (result.status === 'passed') result.status = 'warning';
  } else {
    const stock = log.medical_supplies.initial_stock;
    
    // v2.1 FIX: Calculate MDOS using SupplyIntelligence (same as Tab4 does)
    // This ensures validation shows the same MDOS that Tab4 displays
    let daysOfSupply = 0;
    
    // First try: Use pre-calculated mdos_analysis if available
    // After Tab4_Logistics v8.5.3, this field is written from calculateLogisticsProjection
    // (calculator-derived, correct) rather than SupplyIntelligence (population-based, ~11% rate).
    if (log.medical_supplies.mdos_analysis?.overall_mdos) {
      daysOfSupply = log.medical_supplies.mdos_analysis.overall_mdos;
    }
    // Second try: MDOS-VAL-001 FIX — derive from casualty_projections.supply_chain when
    // mdos_analysis is absent (Tab4 not yet saved). supply_chain is written by calculateCasualties
    // in Tab3 Save — uses actual T1/T2 blood consumption, not population-based rates.
    // Takes the minimum days-remaining across all blood products (limiting factor = MDOS).
    else if (scenario?.casualty_projections?.supply_chain?.days_remaining) {
      const dr = scenario.casualty_projections.supply_chain.days_remaining;
      const daysArr = Object.values(dr)
        .map(v => (typeof v?.days === 'number' ? v.days : Infinity))
        .filter(d => d < Infinity);
      daysOfSupply = daysArr.length > 0 ? Math.min(...daysArr) : 0;
      if (DEBUG) console.log(`✅ Validation calculated MDOS from supply_chain: ${daysOfSupply} days`);
    }
    // Third try: SupplyIntelligence (last resort — population-based, may be inaccurate)
    else if (SupplyIntelligence?.calculateScenarioMDOS) {
      try {
        const mdosResult = SupplyIntelligence.calculateScenarioMDOS(scenario);
        daysOfSupply = mdosResult?.overall_mdos || 0;
        if (DEBUG) console.log(`⚠️ Validation MDOS from SupplyIntelligence (Tab4 not saved): ${daysOfSupply} days`);
      } catch (e) {
        console.warn('Could not calculate MDOS:', e);
        // Fallback to legacy fields
        daysOfSupply = stock.days_of_supply || stock.mdos || 0;
      }
    }
    // Fallback: Legacy fields
    else {
      daysOfSupply = stock.days_of_supply || stock.mdos || 0;
    }
    
    result.details.days_of_supply = daysOfSupply;

    // Use MDOS_STANDARDS from constants
    if (daysOfSupply < thresholds.supply_minimum_days) {
      result.status = 'error';
      result.issues.push({
        severity: 'ERROR',
        field: 'days_of_supply',
        message: `Supply stock critically low: ${daysOfSupply} days (minimum ${thresholds.supply_minimum_days})`,
        fix: 'Increase initial stock or schedule emergency resupply'
      });
    } else if (daysOfSupply < thresholds.supply_adequate_days) {
      result.issues.push({
        severity: 'WARNING',
        field: 'days_of_supply',
        message: `Supply stock below recommended: ${daysOfSupply} days (recommended ${thresholds.supply_adequate_days})`,
        fix: 'Consider increasing initial stock levels'
      });
      if (result.status === 'passed') result.status = 'warning';
    }

    // Validate using constants function if available
    // v2.2: Restore all warnings including tourniquets/morphine - these are critical supplies
    if (validateStockLevels) {
      const stockValidation = validateStockLevels(stock);
      stockValidation.warnings?.forEach(w => {
        result.issues.push({
          severity: 'WARNING',
          field: 'stock_levels',
          message: w,
          fix: 'Review stock levels for all critical items'
        });
        if (result.status === 'passed') result.status = 'warning';
      });
    }
  }

  // Validate THP configuration
  if (!log.theatre_holding_policy) {
    result.issues.push({
      severity: 'WARNING',
      field: 'theatre_holding_policy',
      message: 'Theatre Holding Policy not configured',
      fix: 'Select THP in Tab 4 for RTD projections'
    });
    if (result.status === 'passed') result.status = 'warning';
  } else {
    result.details.thp = log.theatre_holding_policy.policy || 'THP_7_DAY';
  }

  // Validate personnel configuration
  if (!log.personnel) {
    result.issues.push({
      severity: 'WARNING',
      field: 'personnel',
      message: 'Personnel planning not configured',
      fix: 'Configure personnel in Tab 4'
    });
    if (result.status === 'passed') result.status = 'warning';
  }

  return result;
}

// ============================================================================
// CROSS-TAB INTEGRATION VALIDATION
// ============================================================================

function validateCrossTabIntegration(scenario, thresholds) {
  const result = {
    tab: 'Cross-Tab Integration',
    status: 'passed',
    issues: [],
    details: {}
  };

  try {
    // 1. Force size vs Medical capacity check
    // v2.5 FIX (HIGH-001): Use smart bed calculation instead of simple ratio
    const forceSize = getForceStrength(scenario.force);
    if (forceSize > 0 && scenario.medical_configuration?.mtf_structure) {
      const mtf = scenario.medical_configuration.mtf_structure;
      
      let totalBeds = 0;
      // C-009b FIX (March 3, 2026): Role 1 excluded from holding bed total.
      // Role 1 is a throughput point, not a holding facility (AJMedP-1).
      // 6 treatment positions, max 4-hour hold — not inpatient beds.
      // Including role1 inflated capacity total and masked shortfalls at Role 2/3.
      // totalBeds += (mtf.role1?.beds || 0) * (mtf.role1?.count || 0); -- REMOVED
      totalBeds += (mtf.role2?.beds || 20) * (mtf.role2?.count || 0);
      totalBeds += (mtf.role2e?.beds || 40) * (mtf.role2e?.count || 0);
      totalBeds += (mtf.role3?.beds || 100) * (mtf.role3?.count || 0);

      // v2.5 FIX (HIGH-001): Use smart bed calculation that considers:
      // - Operational states (COMBAT/SUPPORT/RESERVE)
      // - Unit types and positions
      // - Posture (ATTACK/DEFENSE)
      // - Theatre Holding Policy
      // - Evacuation to higher echelon
      const bedEstimate = calculateEstimatedBedNeed(scenario);
      
      // Minimum beds = smart estimate (or fallback to 50% of simple ratio if calc fails)
      const minimumBeds = bedEstimate.estimated_beds_needed > 0 
        ? bedEstimate.estimated_beds_needed
        : Math.ceil(forceSize * thresholds.beds_minimum_percent * 0.5);
      
      // VS-08 FIX: Replace `* 1.3` with BED_NEED_FACTORS.SURGE_CAPACITY_MULTIPLIER (= 1.30).
      // Surge capacity doctrine: 30% above normal establishment per Role 2 doctrine.
      const recommendedBeds = Math.ceil(minimumBeds * BED_NEED_FACTORS.SURGE_CAPACITY_MULTIPLIER);
      
      result.details.force_size = forceSize;
      result.details.total_beds = totalBeds;
      result.details.recommended_beds = recommendedBeds;
      result.details.minimum_beds = minimumBeds;
      result.details.bed_calculation = bedEstimate;

      if (totalBeds < minimumBeds * 0.7) {
        // Only error if significantly below (< 70% of estimated need)
        result.status = 'error';
        result.issues.push({
          severity: 'ERROR',
          field: 'medical_capacity',
          message: `Insufficient medical capacity: ${totalBeds} beds for estimated need of ${minimumBeds}`,
          fix: `Add MTF facilities to reach at least ${minimumBeds} beds (recommended: ${recommendedBeds})`,
          calculation_method: bedEstimate.using_smart_calc ? 'Smart (based on operational context)' : 'Fallback ratio'
        });
      } else if (totalBeds < minimumBeds) {
        // Warning if below estimate but within tolerance
        result.issues.push({
          severity: 'WARNING',
          field: 'medical_capacity',
          message: `Limited surge capacity: ${totalBeds} beds vs estimated need of ${minimumBeds}`,
          fix: `Consider adding MTF capacity for surge (recommended: ${recommendedBeds} beds)`
        });
        if (result.status === 'passed') result.status = 'warning';
      }
      // Note: If totalBeds >= minimumBeds, no warning - beds are adequate!
    }

    // 2. Operation duration vs Supply days check
    if (scenario.operation_parameters?.duration_days && 
        scenario.logistics?.medical_supplies?.initial_stock) {
      const duration = scenario.operation_parameters.duration_days;
      
      // v2.1 FIX: Calculate MDOS using SupplyIntelligence (same method as Tab4)
      // MDOS-VAL-001 FIX (v2.12): prefer supply_chain over SupplyIntelligence fallback
      let daysOfSupply = 7; // Default
      if (scenario.logistics.medical_supplies.mdos_analysis?.overall_mdos) {
        daysOfSupply = scenario.logistics.medical_supplies.mdos_analysis.overall_mdos;
      } else if (scenario?.casualty_projections?.supply_chain?.days_remaining) {
        const dr = scenario.casualty_projections.supply_chain.days_remaining;
        const daysArr = Object.values(dr)
          .map(v => (typeof v?.days === 'number' ? v.days : Infinity))
          .filter(d => d < Infinity);
        daysOfSupply = daysArr.length > 0 ? Math.min(...daysArr) : 7;
      } else if (SupplyIntelligence?.calculateScenarioMDOS) {
        try {
          const mdosResult = SupplyIntelligence.calculateScenarioMDOS(scenario);
          daysOfSupply = mdosResult?.overall_mdos || 7;
        } catch (e) {
          daysOfSupply = resolveInput(scenario.logistics.medical_supplies.initial_stock.days_of_supply, 'medical_days_of_supply', { zeroIsMissing: true });
        }
      }
      
      const resupplyEvents = scenario.logistics.medical_supplies.resupply_events || [];

      result.details.operation_duration = duration;
      result.details.initial_supply_days = daysOfSupply;
      result.details.resupply_events_count = resupplyEvents.length;

      if (daysOfSupply < duration && resupplyEvents.length === 0) {
        result.issues.push({
          severity: 'WARNING',
          field: 'resupply_planning',
          message: `${duration}-day operation but only ${daysOfSupply} days of supply (no resupply scheduled)`,
          fix: 'Schedule resupply events in Tab 4 or increase initial stock'
        });
        if (result.status === 'passed') result.status = 'warning';
      }
    }

    // 3. THP vs Evacuation capability check
    if (scenario.logistics?.theatre_holding_policy && 
        scenario.medical_configuration?.evacuation_assets) {
      const thpPolicy = scenario.logistics.theatre_holding_policy.policy || 'THP_7_DAY';
      const thpConfig = THEATRE_HOLDING_POLICY[thpPolicy];
      
      if (thpConfig?.strategic_evac_required) {
        const airAssets = 
          (scenario.medical_configuration.evacuation_assets.air?.iar330_puma?.count || 0) +
          (scenario.medical_configuration.evacuation_assets.air?.uh60_blackhawk?.count || 0);
        
        if (airAssets === 0) {
          result.issues.push({
            severity: 'WARNING',
            field: 'thp_evac_mismatch',
            message: `${thpConfig.label} requires strategic evacuation but no air assets configured`,
            fix: 'Add air evacuation assets or extend THP'
          });
          if (result.status === 'passed') result.status = 'warning';
        }
      }
    }

    // 4. Evacuation assets vs force size - v2.1 FIX: Use helper for property name compatibility
    const evacForceSize = getForceStrength(scenario.force);
    if (evacForceSize > 0 && 
        scenario.medical_configuration?.evacuation_assets) {
      const assets = scenario.medical_configuration.evacuation_assets;
      
      const groundAmbulances = 
        (assets.ground?.light_ambulances?.count || 0) +
        (assets.ground?.armored_ambulances?.count || 0);

      result.details.ground_ambulances = groundAmbulances;

      const minAmbulances = Math.ceil(evacForceSize / thresholds.ambulances_per_personnel);
      
      if (groundAmbulances < minAmbulances * 0.5) {
        result.issues.push({
          severity: 'WARNING',
          field: 'evacuation_capacity',
          message: `Limited evacuation capacity: ${groundAmbulances} ambulances for ${evacForceSize} personnel`,
          fix: `Consider adding more evacuation assets (recommended: ~${minAmbulances} ambulances)`
        });
        if (result.status === 'passed') result.status = 'warning';
      }
    }

  } catch (error) {
    console.error('Error in cross-tab validation:', error);
    result.issues.push({
      severity: 'WARNING',
      field: 'integration_check',
      message: 'Error validating cross-tab integration',
      fix: 'Review all tab configurations'
    });
    if (result.status === 'passed') result.status = 'warning';
  }

  return result;
}

// ============================================================================
// RECOMMENDATIONS GENERATOR
// ============================================================================

function generateValidationRecommendations(validation) {
  const recommendations = [];

  // Critical errors
  if (validation.overall_status === 'red') {
    recommendations.push({
      priority: 'CRITICAL',
      message: `${validation.summary.errors} critical error(s) found`,
      action: 'Fix all errors before proceeding to Tab 5',
      details: 'See detailed issues above for specific fixes'
    });
  }

  // Warnings
  if (validation.summary.warnings > 0) {
    recommendations.push({
      priority: 'HIGH',
      message: `${validation.summary.warnings} warning(s) found`,
      action: 'Review warnings for optimal configuration',
      details: 'Scenario will function but may not be optimal'
    });
  }

  // All passed
  if (validation.overall_status === 'green') {
    recommendations.push({
      priority: 'INFO',
      message: 'All validation checks passed',
      action: 'Ready to proceed to Tab 5: Calculations & Reports',
      details: 'Configuration is complete and validated'
    });
  }

  // Specific recommendations based on issues
  Object.values(validation.checks).forEach(check => {
    if (check && check.issues) {
      check.issues.forEach(issue => {
        if (issue.severity === 'ERROR') {
          recommendations.push({
            priority: 'CRITICAL',
            message: `${check.tab}: ${issue.message}`,
            action: issue.fix,
            field: issue.field
          });
        }
      });
    }
  });

  return recommendations;
}

// ============================================================================
// QUICK VALIDATION (for real-time UI feedback)
// ============================================================================

/**
 * Quick validation check (lightweight, for real-time UI feedback)
 * @param {object} scenario - Complete scenario object
 * @returns {string} Status: 'green' | 'yellow' | 'red'
 */
export function quickValidationCheck(scenario) {
  if (!scenario) return 'red';
  
  let errorCount = 0;
  let warningCount = 0;

  // Critical checks - v2.1 FIX: Use helper for property name compatibility
  if (!scenario.force || getForceStrength(scenario.force) === 0) errorCount++;
  if (!scenario.operation_parameters || !scenario.operation_parameters.duration_days) errorCount++;
  if (!scenario.medical_configuration || !scenario.medical_configuration.mtf_structure) errorCount++;

  // Warning checks using MDOS_STANDARDS
  // v2.1 FIX: Calculate MDOS using SupplyIntelligence if not pre-calculated
  // MDOS-VAL-001 FIX (v2.12): prefer supply_chain over SupplyIntelligence fallback
  let daysOfSupply = scenario.logistics?.medical_supplies?.mdos_analysis?.overall_mdos;
  
  // If not pre-calculated, try to calculate it
  if (!daysOfSupply && scenario.logistics?.medical_supplies?.initial_stock) {
    if (scenario?.casualty_projections?.supply_chain?.days_remaining) {
      const dr = scenario.casualty_projections.supply_chain.days_remaining;
      const daysArr = Object.values(dr)
        .map(v => (typeof v?.days === 'number' ? v.days : Infinity))
        .filter(d => d < Infinity);
      daysOfSupply = daysArr.length > 0 ? Math.min(...daysArr) : undefined;
    } else if (SupplyIntelligence?.calculateScenarioMDOS) {
      try {
        const mdosResult = SupplyIntelligence.calculateScenarioMDOS(scenario);
        daysOfSupply = mdosResult?.overall_mdos;
      } catch (e) {
        // Fallback - if supplies exist, assume reasonable MDOS
        const stock = scenario.logistics.medical_supplies.initial_stock;
        if (stock.iv_fluids_liters > 0 || stock.prbc_units > 0) {
          daysOfSupply = 10; // Assume reasonable if supplies exist
        }
      }
    }
  }
  
  if (daysOfSupply && daysOfSupply < (requireNumber(MDOS_STANDARDS?.STATUS?.CRITICAL?.days, 'validationSystem', 'MDOS_STANDARDS.STATUS.CRITICAL.days'))) {
    warningCount++;
  }
  
  if (errorCount > 0) return 'red';
  if (warningCount > 0) return 'yellow';
  return 'green';
}

/**
 * Get validation status color
 */
export function getStatusColor(status) {
  const colors = {
    green: '#4caf50',
    yellow: '#ff9800',
    red: '#f44336',
    passed: '#4caf50',
    warning: '#ff9800',
    error: '#f44336'
  };
  return colors[status] || '#757575';
}

/**
 * Get validation status icon
 */
export function getStatusIcon(status) {
  const icons = {
    green: 'CheckCircle',
    yellow: 'Warning',
    red: 'Error',
    passed: 'CheckCircle',
    warning: 'Warning',
    error: 'Error'
  };
  return icons[status] || 'Help';
}

// ============================================================================
// EXPORT ALL
// ============================================================================

const validationSystem = {
  runCompleteValidation,
  quickValidationCheck,
  getStatusColor,
  getStatusIcon
};

export default validationSystem;
